import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-CLAUDE.md: progress-store. Local cache of mastery + visits per concept.
 * Server-side canon lives behind RFC-0006 (Student State, mastery facet).
 */
type MasteryTier = 'unseen' | 'exposed' | 'practicing' | 'mastered';

interface ProgressState {
  mastery: Record<string, MasteryTier>;
  visits: Record<string, number>;
  bumpVisit: (conceptId: string) => void;
  setMastery: (conceptId: string, tier: MasteryTier) => void;
  /**
   * Bulk-seed mastery (e.g. from the cold-start diagnostic). Only writes
   * concepts that are currently 'unseen' or absent — never overwrites earned
   * mastery, so re-running the diagnostic won't downgrade what the student
   * has already practiced.
   */
  seedMastery: (seed: Record<string, MasteryTier>) => void;
}

export const useProgress = create<ProgressState>()(
  persist(
    (set) => ({
      mastery: {},
      visits: {},
      bumpVisit: (conceptId) =>
        set((s) => ({ visits: { ...s.visits, [conceptId]: (s.visits[conceptId] ?? 0) + 1 } })),
      setMastery: (conceptId, tier) =>
        set((s) => ({ mastery: { ...s.mastery, [conceptId]: tier } })),
      seedMastery: (seed) =>
        set((s) => {
          const next = { ...s.mastery };
          for (const [id, tier] of Object.entries(seed)) {
            const existing = next[id];
            if (!existing || existing === 'unseen') next[id] = tier;
          }
          return { mastery: next };
        }),
    }),
    { name: 'disha.progress' },
  ),
);
